/** The emailed link lands here: exchange the one-time token for a session, then go to the safe `returnTo` or the person's landing page. */
import { useEffect, useRef, useState } from "react";
import type { ErrorBody } from "@bsim/contracts";
import type { RouteCall } from "../api/types";
import { landingPath } from "../shell/landing";
import { Link, navigate, useLocation } from "../shell/history";
import { GxLoading } from "../theme/GxLoading";
import { GxErrorBanner } from "../theme/primitives";
import { AuthLayout } from "./AuthLayout";
import { authHref, rememberSignedIn, SIGN_IN_PATH, safeReturnTo } from "./returnTo";

const NO_TOKEN: ErrorBody["error"] = { code: "LINK_INVALID_OR_EXPIRED", message: "No token in the link", requestId: "" };

export function VerifyPage({ verify }: { readonly verify: RouteCall<"authVerifyMagicLink"> }) {
  const params = new URLSearchParams(useLocation().search);
  const token = params.get("token") ?? "";
  const returnTo = safeReturnTo(params.get("returnTo"));
  const [error, setError] = useState<ErrorBody["error"] | null>(token === "" ? NO_TOKEN : null);
  const started = useRef(false);

  useEffect(() => {
    if (token === "" || started.current) return; // the token works once: never send it twice
    started.current = true;
    void verify(undefined, { token }).then((result) => {
      if (result.ok) { rememberSignedIn(); navigate(returnTo ?? landingPath(result.value.principal) ?? "/", { replace: true }); } else setError(result.error.code === "VALIDATION" ? { ...result.error, code: "LINK_INVALID_OR_EXPIRED" } : result.error); // a mangled token is a bad link to the reader
    });
  }, [token, returnTo, verify]);

  return (
    <AuthLayout>
      <div className="gx-eyebrow">Sign in</div>
      {error === null ? (
        <div role="status" data-testid="auth.verifying">
          <h1 className="gx-h1 gx-auth-h">Signing you in</h1>
          <GxLoading words="Signing you in…" height={72} />
        </div>
      ) : (
        <>
          <h1 className="gx-h1 gx-auth-h">We couldn&apos;t sign you in</h1>
          <GxErrorBanner error={error} />
          <div className="gx-auth-acts">
            <Link href={authHref(SIGN_IN_PATH, returnTo)} className="gx-btn gx-btn--primary gx-btn--lg" data-testid="auth.newLink">Send me a new link</Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
